import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import translations, { Language, languages, LanguageOption } from '@/lib/i18n/translations';

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: keyof typeof translations['en']) => string;
  dir: 'ltr' | 'rtl';
  languages: LanguageOption[];
  currentLanguage: LanguageOption;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

const STORAGE_KEY = 'pos-language';

export function LanguageProvider({ children }: { children: ReactNode }) {
  const [language, setLanguageState] = useState<Language>(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved && ['en', 'ur', 'ar', 'zh', 'hi', 'sd', 'bal', 'ps'].includes(saved)) {
        return saved as Language;
      }
    }
    return 'en';
  });

  const currentLanguage = languages.find(l => l.code === language) || languages[0];

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, language);
    // Update document direction for RTL languages
    document.documentElement.dir = currentLanguage.dir;
    document.documentElement.lang = language;
  }, [language, currentLanguage.dir]);

  const setLanguage = (lang: Language) => {
    setLanguageState(lang);
  };

  const t = (key: keyof typeof translations['en']): string => {
    return translations[language]?.[key] || translations['en'][key] || key;
  };

  return (
    <LanguageContext.Provider 
      value={{ 
        language, 
        setLanguage, 
        t, 
        dir: currentLanguage.dir,
        languages,
        currentLanguage
      }}
    >
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
}
